import SAPOMailConfig          from '@/SAPOMailConfig';
import HTTPResponseEntity      from './Entities/HTTPResponseEntity';
import qs                      from 'qs';
import VersionController       from '@/js/Webmail/VersionController';
import ClientLoggingController from '@/js/Webmail/ClientLogging/ClientLoggingController';
import BindSession from '@/js/Webmail/BindSession';

export { HTTPServiceSingleton as default};

class HTTPService {
  /**
   * Saves current request data
   * to prevent duplicated request
   *
   * @private
   *
   * @type {Object<string, HTTPResponseEntity>}
   *
   * @since 7.0.0
   */
  current_requests = {};

  /**
   * Uploads a file via HTTP using the EventUpload and FormData objects
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String}      endpoint   server endpoint
   * @param {EventUpload} event      event upload
   * @param {FormData}    form_data  form data
   * @param {Object}      headers    request headers
   *
   * @return {Promise<void>}
   */
  async uploadFormData(endpoint, event, form_data, headers = {}) {
    form_data.append('token', SAPOMailConfig.server.security.csrf_token);
    this.uploadFile(endpoint, event, form_data, headers);
  }

  /**
   * Uploads a file via HTTP using the EventUpload and Blob Data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String}      endpoint   server endpoint
   * @param {EventUpload} event      event upload
   * @param {Blob}        blob       blob data
   * @param {Object}      headers    request headers
   * @param {String}      type       request type
   *
   * @return {Promise<void>}
   */
  async uploadBlob(endpoint, event, blob, headers = {}, type = 'POST') {
    this.uploadFile(endpoint, event, blob, headers, type);
  }

  /**
   * Uploads a file via HTTP using the EventUpload and FormData objects
   *
   * @since 7.0.0
   *
   * @param {String}      endpoint   server endpoint
   * @param {EventUpload} event      event upload
   * @param {FormData}    form_data  form data
   * @param {Object}      headers    request headers
   *
   * @return {Promise<void>}
   */
  uploadFile(endpoint, event, data, headers = {}, type = 'POST') {
    let service = this;

    event.xhr.addEventListener("load", function(response) {
      let entity = new HTTPResponseEntity();

      if (response.target.status !== 200) {
        entity.success  = false;
        entity.messages = [
          {
            type    : 'horde.error',
            message : 'notifications.http_client_error'
          }
        ];

        event.setFinished(entity);
        return;
      }

      service.parseOldResponse(entity, response.target.responseText ? JSON.parse(response.target.responseText) : null);
      event.setFinished(entity);
    });

    event.xhr.open(type, endpoint);

    for (let header in headers) {
      event.xhr.setRequestHeader(header, headers[header]);
    }

    event.xhr.send(data);
  }

  /**
   * Makes a post request using form url encoded data
   *
   * All requests with the same id will wait for the first request
   * to finish and return the same response.
   *
   * @since 7.0.0
   *
   * @param {String} endpoint   server endpoint
   * @param {Object} data       post data key => value
   * @param {String} request_id request id
   *
   * @throws {Error} request failed
   * @returns void
   */
  async oldPost(endpoint, data = {}, request_id = null) {
    data.token   = SAPOMailConfig.server.security.csrf_token;
    let params   = {
      method     : "POST",
      mode       : "cors",
      cache      : "no-cache",
      credentials: "same-origin",
      headers    : {
        'content-type'    : 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        'X-A'             : 1,
      },
      body       : qs.stringify(data),
    };

    if (this.current_requests[request_id]) {
      let entity = this.current_requests[request_id];
      await this.awaitForRequest(request_id);
      return entity;
    }

    try {
      var entity   = this.setCurrentRequest(request_id);
      var response = await fetch(endpoint, params);
      let results  = await response.json();
      this.parseOldResponse(entity, results);

      VersionController.requestHeaderVersion(response.headers);
      ClientLoggingController.addEntry({
        type     : request_id,
        endpoint : endpoint,
        // request  : data,
        // results  : entity
      });
    } catch (error) {
      // @todo - Do something with Server Errors
      entity.success  = false;
      entity.messages = [
        {
          type    : 'horde.error',
          message : 'notifications.http_client_error'
        }
      ];
    }

    this.unsetCurrentRequest(request_id);

    let location = null;

    if (response && response.headers) {
      location = response.headers.get('X-PTMAIL-LOCATION');
    }

    if (location !== null) {
      window.location = location;
    }

    return entity;
  }

  /**
   * Makes a post request using form url encoded data
   *
   * All requests with the same id will wait for the first request
   * to finish and return the same response.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} endpoint   server endpoint
   * @param {Object} data       post data key => value
   * @param {String} request_id request id
   *
   * @returns {Promise<HTTPResponseEntity>}
   */
  async post(endpoint, data, request_id = null, log = true, session_refresh = true) {
    if (session_refresh) {
      await BindSession.refresh();
    }

    data = (!data) ? {} : data;

    if (SAPOMailConfig.server.security) {
      data.token = SAPOMailConfig.server.security.csrf_token;
    }

    let params   = {
      method     : "POST",
      mode       : "cors",
      cache      : "no-cache",
      credentials: "same-origin",
      headers    : {
        'content-type'    : 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        'X-A'             : 1,
      },
      body: qs.stringify(data),
    };

    if (this.current_requests[request_id]) {
      let entity = this.current_requests[request_id];
      await this.awaitForRequest(request_id);
      return entity;
    }

    try {
      var entity   = this.setCurrentRequest(request_id);
      var response = await fetch(endpoint, params);
      let results  = await response.json();

      this.parseResponse(entity, results);
      VersionController.requestHeaderVersion(response.headers);

      if (log) {
        ClientLoggingController.addEntry({
          type     : request_id,
          endpoint : endpoint,
          // request  : data,
          // results  : entity
        });
      }
    } catch (error) {
      entity.success  = false;
      entity.messages = {
        error : 'http_client_error',
      };
    }

    this.unsetCurrentRequest(request_id);

    let location = null;

    if (response && response.headers) {
      location = response.headers.get('X-PTMAIL-LOCATION');
    }

    if (location !== null) {
      window.location = location;
    }

    return entity;
  }

  /**
   * Makes a get request to a remote url
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} endpoint   server endpoint
   *
   * @returns {Promise<HTTPResponseEntity>}
   */
  async get(endpoint, add_token = false, json = false) {
    let entity   = new HTTPResponseEntity();
    let params   = {
      method     : "GET",
      mode       : "cors",
      cache      : "no-cache",
      credentials: "same-origin",
    };

    if (add_token && SAPOMailConfig.server.security) {
      endpoint += (endpoint.includes('?') ? '&' : '?') + 'view_token=' + SAPOMailConfig.server.security.csrf_token;
    }

    try {
      let response = await fetch(endpoint, params);
      let results  = await response.text();

      entity.success  = true;
      entity.results  = results;

    } catch (error) {
      // @todo - Do something with Server Errors
      entity.success  = false;
      entity.messages = {
        error : 'http_client_error',
      };
    }

    return entity;
  }

  /**
   * Parses HTTP Response into the an HTTPResponseEntity
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {HTTPResponseEntity} entity  entity to parse the results into
   * @param {Object}             results http raw results
   *
   * @returns {void}
   */
  parseResponse(entity, results) {
    if(!results.response || typeof results.response.success === undefined ) {
      throw Error('Server results are empty');
    }

    entity.success  = results.response.success;
    entity.results  = results.response.results  || undefined;
    entity.messages = results.response.messages || undefined;
  }

  /**
   * Parses HTTP Response into the an HTTPResponseEntity
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {HTTPResponseEntity} entity  entity to parse the results into
   * @param {Object}             results http raw results
   *
   * @returns {void}
   */
  parseOldResponse(entity, results) {
    entity.success  = true;

    if(!results || typeof results.response === 'undefined' ) {
      entity.results = results;
      return;
    }

    entity.results  = results.tasks || {};
    entity.messages = results.msgs  || [];

    if (results.response.messages && results.response.messages.error) {
      entity.messages.push({type : 'horde.error', message : 'notifications.' + results.response.messages.error});
    }

    if (results.response && results.response.action) {
      entity.results['imp:' + results.response.action] = results.response;
    }
  }

  /**
   * Sets the current request
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {string|null} request_id
   *
   * @returns {HTTPResponseEntity}
   */
  setCurrentRequest(request_id)
  {
    if (request_id === null) {
      return new HTTPResponseEntity();
    }

    if (this.current_requests[request_id]) {
      return this.current_requests[request_id];
    }

    this.current_requests[request_id] = new HTTPResponseEntity();
    return this.current_requests[request_id];
  }

  /**
   * Deletes the current request
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {string|null} request_id
   *
   * @returns {HTTPResponseEntity}
   */
  unsetCurrentRequest(request_id)
  {
    delete this.current_requests[request_id];
  }

  /**
   * Waits for a request with the same id to be finished
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String|null} request_id request id
   * @param {Number}      timeout    wait timeout in ms
   *
   * @returns {Promise<HTTPResponseEntity>}
   */
  async awaitForRequest(request_id, timeout = 100)
  {
    if (!this.current_requests[request_id]) {
      return;
    }

    return new Promise(resolve => {
      setTimeout(() => {
        resolve(this.awaitForRequest(request_id));
      }, timeout);
    })
  }
}

/**
 * HTTP Service Class for internal requests
 *
 * All Ajax calls are made via JS native fetch API
 *
 * Responses\Requests are pre parsed according to Account Registration API Rules
 *
 * @since 7.0.0
 */
const HTTPServiceSingleton = new HTTPService();